import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Ticket, Star, Mail, ArrowLeft, LogOut, ShieldAlert } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/check", { withCredentials: true }).then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (isAdmin === false) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="max-w-md text-center border border-brand-gold/30 bg-brand-ivory-soft p-10">
          <ShieldAlert size={40} strokeWidth={1.2} className="mx-auto text-brand-maroon" />
          <p className="label-tiny text-brand-gold mt-4">Restricted Zone</p>
          <h1 className="font-serif-brand text-3xl text-brand-charcoal mt-2">Admin access required</h1>
          <p className="text-sm text-brand-muted mt-3">This area is for Bindni team members. Ask an admin to whitelist <code className="text-brand-maroon">{user.email}</code>.</p>
          <Link to="/" className="btn-primary inline-block mt-6">Back to Shop</Link>
        </div>
      </div>
    );
  }

  if (isAdmin === null) return <div className="min-h-[50vh] flex items-center justify-center text-brand-muted">Checking privileges…</div>;

  return (
    <div className="min-h-screen grid lg:grid-cols-[240px_1fr]" data-testid="admin-layout">
      <aside className="bg-brand-maroon-deep text-brand-ivory p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-2 text-brand-ivory/70 hover:text-brand-gold text-xs tracking-[0.2em] uppercase" data-testid="admin-back-to-shop">
          <ArrowLeft size={14} /> Back to Shop
        </Link>
        <div className="mt-6 mb-10">
          <p className="font-serif-brand text-3xl">Bindni</p>
          <p className="label-tiny text-brand-gold mt-1">Admin Console</p>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-brand-maroon text-brand-gold" : "text-brand-ivory/80 hover:bg-brand-maroon/50 hover:text-brand-ivory"}`}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={16} strokeWidth={1.5} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 pt-6 border-t border-brand-gold/25 text-xs">
          <p className="text-brand-ivory/60 truncate">{user.email}</p>
          <button onClick={logout} className="mt-3 text-brand-ivory/80 hover:text-brand-gold flex items-center gap-2" data-testid="admin-logout"><LogOut size={12} /> Logout</button>
        </div>
      </aside>
      <div className="bg-brand-ivory-soft min-h-screen">
        <Outlet />
      </div>
    </div>
  );
}
